/**
 * Централизованный HTTP-клиент: Bearer access, `credentials: 'include'` для httpOnly refresh cookie, refresh при 401 (кроме `/auth/*`).
 * Не используйте сырой `fetch` для API — только `api.*`.
 *
 * @example
 * ```ts
 * const data = await api.get(`/projects/${projectId}`);
 * await api.post('/auth/login', { email, password });
 * const blob = await api.getBlob(`/projects/${id}/export`);
 * ```
 */
import { PLANS, RT_CLIENT_HEADER, type PaywallReason, type PlanId } from '@sloy/shared';
import { useAuthStore, type User } from '@/stores/authStore';
import { queryClient } from '@/lib/queryClient';
import { realtimeClientId } from '@/features/realtime/realtimeClientId';
import { usePaywallStore } from '@/stores/paywallStore';
import { editorText } from '@/i18n/editorTranslations';

/** Ошибка API с опциональным `code` из тела ответа (например `EMAIL_NOT_VERIFIED`). */
export class ApiRequestError extends Error {
  readonly code?: string;
  /** id из массового эндпоинта, чей `updatedAt` разошёлся с присланным (409, см. §1.7). */
  readonly conflicts?: string[];
  /** HTTP-статус ответа: опрос задачи отличает «задачи нет» (404) от сбоя сервера (5xx). */
  readonly status?: number;

  constructor(message: string, code?: string, conflicts?: string[], status?: number) {
    super(message);
    this.name = 'ApiRequestError';
    this.code = code;
    this.conflicts = conflicts;
    this.status = status;
  }
}

/**
 * Серверный отказ по тарифу (`billing-gate.ts`) открывает paywall-модалку прямо
 * здесь: UI-гейты — косметика, а этот перехват гарантирует, что ЛЮБОЙ доступ к
 * платной фиче в обход кнопок упрётся в то же окно с тарифами.
 */
function maybeOpenPaywall(err: unknown): void {
  // Тело ошибки может оказаться `null` или примитивом — тогда это заведомо не тарифный отказ.
  if (typeof err !== 'object' || err === null) {
    return;
  }
  const e = err as {
    code?: string;
    feature?: string;
    requiredPlan?: string;
    workspaceId?: string;
  };
  if (e.code === 'plan_limit' && typeof e.feature === 'string') {
    // Тариф — только из каталога: незнакомую строку стор сохранил бы как есть, и модалка,
    // читающая `PLANS[requiredPlan]`, упала бы. Нет такого — стор выберет тариф сам.
    const requiredPlan =
      typeof e.requiredPlan === 'string' && Object.hasOwn(PLANS, e.requiredPlan)
        ? (e.requiredPlan as PlanId)
        : undefined;
    usePaywallStore
      .getState()
      .open(
        e.feature as PaywallReason,
        requiredPlan,
        typeof e.workspaceId === 'string' ? e.workspaceId : null,
      );
  }
}

export interface MapStyleRecord {
  id: string;
  projectId: string;
  name: string;
  patch: unknown;
  createdAt: string;
}

/** Стиль из списка «все проекты пользователя» (импорт из другого проекта). */
export interface UserMapStyleRecord extends MapStyleRecord {
  projectName: string;
}

/**
 * Базовый URL API для fetch.
 * - По умолчанию `/api` — тот же origin (dev: Vite proxy, prod: nginx и т.п.).
 * - Если задан `VITE_API_BASE` (например `https://api.example.com/api`) — запросы идут туда.
 */
function resolveApiBase(): string {
  const raw = import.meta.env.VITE_API_BASE;
  if (typeof raw === 'string' && raw.trim().length > 0) {
    return raw.trim().replace(/\/$/, '');
  }
  return '/api';
}

export const API_BASE = resolveApiBase();

/**
 * Эндпоинты, для которых НЕ нужно перехватывать 401
 * (логин/регистрация сами возвращают 401 при неверных данных —
 *  это не повод разлогинивать пользователя и перезагружать страницу).
 */
const AUTH_ENDPOINTS = [
  '/auth/login',
  '/auth/register',
  '/auth/verify-email',
  '/auth/resend-verification',
  '/auth/refresh',
  '/auth/logout',
  '/auth/forgot-password',
  '/auth/reset-password',
];

function isAuthEndpoint(path: string): boolean {
  return AUTH_ENDPOINTS.some((ep) => path.startsWith(ep));
}

/** Флаг, предотвращающий параллельные refresh-запросы */
let refreshPromise: Promise<boolean> | null = null;

/** Ответы этих запросов ставят или стирают refresh-cookie. */
const REFRESH_COOKIE_ENDPOINTS = [
  '/auth/login',
  '/auth/verify-email',
  '/auth/refresh',
  '/auth/logout',
  '/auth/me/password',
  '/auth/me/email/confirm',
];

/**
 * Сколько запрос ждёт предыдущий, прежде чем его отменить: иначе без ответа сервера не войти вообще.
 * Отмена, а не обгон: ответ обогнанного запроса всё равно поставил бы свою cookie поверх новой.
 */
export const REFRESH_COOKIE_WAIT_MS = 15_000;

interface RefreshCookieWrite {
  settled: Promise<unknown>;
  abort: () => void;
}

let lastRefreshCookieWrite: RefreshCookieWrite | null = null;

/**
 * Запросы, пишущие refresh-cookie, идут по одному, в порядке вызова. Ответ refresh, опоздавший ко
 * входу, ставил свою cookie поверх новой: вкладка его отбрасывает, а при следующем обмене сервер
 * отвечал уже прошлым аккаунтом, и новый вылетал из сессии.
 */
function serializeRefreshCookieWrite<T>(send: (signal: AbortSignal) => Promise<T>): Promise<T> {
  const previous = lastRefreshCookieWrite;
  const controller = new AbortController();
  const waitForPrevious = async () => {
    if (!previous) {
      return;
    }
    const timer = setTimeout(previous.abort, REFRESH_COOKIE_WAIT_MS);
    await previous.settled;
    clearTimeout(timer);
  };
  const result = waitForPrevious().then(() => send(controller.signal));
  lastRefreshCookieWrite = { settled: result.catch(() => undefined), abort: () => controller.abort() };
  return result;
}

interface RefreshResponseBody {
  tokens: { accessToken: string };
  user?: User;
}

function applyRefreshResponseBody(data: RefreshResponseBody): void {
  const { setAuth, updateTokens } = useAuthStore.getState();
  if (data.user) {
    setAuth(data.user, data.tokens);
  } else {
    updateTokens(data.tokens);
  }
}

/**
 * Обмен httpOnly refresh cookie на access (и пользователя — ответ как у login).
 * Refresh по-прежнему не попадает в JS.
 */
async function fetchRefreshFromCookie(): Promise<RefreshResponseBody | null> {
  try {
    const res = await serializeRefreshCookieWrite((signal) =>
      fetch(`${API_BASE}/auth/refresh`, {
        method: 'POST',
        credentials: 'include',
        signal,
      }),
    );

    if (!res.ok) {
      return null;
    }

    return (await res.json()) as RefreshResponseBody;
  } catch {
    return null;
  }
}

/**
 * Стереть refresh-cookie, не трогая состояние вкладки. Ответ refresh, опоздавший к выходу,
 * успевает поставить прошлой сессии свежую cookie поверх стёртой — и при следующем открытии
 * сайта она вернула бы прошлый аккаунт.
 */
function clearRefreshCookie(): void {
  void serializeRefreshCookieWrite((signal) =>
    fetch(`${API_BASE}/auth/logout`, { method: 'POST', credentials: 'include', signal }),
  ).catch(() => undefined);
}

async function executeRefreshTokenExchange(): Promise<boolean> {
  const { sessionEpoch, user: expectedUser } = useAuthStore.getState();

  const data = await fetchRefreshFromCookie();
  const { logout, isAuthenticated } = useAuthStore.getState();
  // Пока шёл обмен, вкладка вышла или вошла другим аккаунтом (`sessionEpoch`): ответ — от
  // прошлой сессии. Не применяем ни его, ни отказ: первый вернул бы прошлый аккаунт, второй
  // разлогинил бы новый.
  if (sessionEpoch !== useAuthStore.getState().sessionEpoch) {
    if (data && !isAuthenticated) {
      clearRefreshCookie();
    }
    return false;
  }
  if (!data) {
    logout();
    return false;
  }
  // Cookie уже другого аккаунта (в соседней вкладке вышли и вошли заново). Сменить личность
  // молча под открытыми данными прошлого аккаунта нельзя: сессия вкладки кончается, вход заново.
  if (expectedUser && data.user && data.user.id !== expectedUser.id) {
    logout();
    return false;
  }

  applyRefreshResponseBody(data);
  return true;
}

/**
 * Обновление access через httpOnly refresh cookie. Один общий in-flight запрос
 * на всех вызывающих (bootstrap при старте и retry после 401).
 */
async function tryRefreshToken(): Promise<boolean> {
  if (!refreshPromise) {
    refreshPromise = executeRefreshTokenExchange().finally(() => {
      refreshPromise = null;
    });
  }
  return refreshPromise;
}

/** Восстановление сессии после гидрации persist (новая вкладка / перезагрузка). */
export async function tryRestoreSessionFromRefreshCookie(): Promise<boolean> {
  return tryRefreshToken();
}

/**
 * Выполняет HTTP-запрос с автоматическим retry при 401.
 * При провале refresh разлогинивает и перенаправляет на /login.
 * @param performFetch - функция, принимающая headers и возвращающая Response
 * @param path - путь запроса (для проверки isAuthEndpoint)
 */
async function withAuthRetry(
  performFetch: (headers: Record<string, string>) => Promise<Response>,
  path: string,
): Promise<Response> {
  const { accessToken: token, sessionEpoch } = useAuthStore.getState();
  // Id вкладки: сервер эхом кладёт его в realtime-события, автор их пропускает.
  const headers: Record<string, string> = { [RT_CLIENT_HEADER]: realtimeClientId };
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  const res = await performFetch(headers);

  if (res.status !== 401) {
    return res;
  }

  if (isAuthEndpoint(path)) {
    return res;
  }

  // Запрос ушёл от прошлой сессии вкладки: повтор шёл бы с токеном уже другого аккаунта, а
  // провал refresh стёр бы его кэш и увёл на /login. Отдаём 401 как есть.
  if (sessionEpoch !== useAuthStore.getState().sessionEpoch) {
    return res;
  }

  const refreshed = await tryRefreshToken();

  if (refreshed) {
    const newToken = useAuthStore.getState().accessToken;
    const retryHeaders = { ...headers, Authorization: `Bearer ${newToken}` };
    return performFetch(retryHeaders);
  }

  // Обмен не удался, потому что, пока он шёл, во вкладке вошёл другой аккаунт: его кэш и
  // страницу не трогаем.
  if (useAuthStore.getState().isAuthenticated) {
    return res;
  }

  queryClient.clear();
  window.location.href = '/login';
  throw new Error(editorText('Сессия истекла'));
}

/**
 * Тело ответа с ошибкой — всегда объект. Не-JSON и JSON-примитив (`null`, строка) дают
 * `{ error: fallback }`: чтение полей у `null` бросило бы TypeError вместо ошибки запроса.
 */
async function readErrorBody(res: Response, fallback: string): Promise<Record<string, unknown>> {
  const body: unknown = await res.json().catch(() => null);
  return typeof body === 'object' && body !== null ? (body as Record<string, unknown>) : { error: fallback };
}

async function fetchApi(path: string, options: RequestInit = {}) {
  const res = await withAuthRetry((authHeaders) => {
    // `RequestInit.headers` — это `HeadersInit`: объект, `Headers` или массив пар.
    // Спред работает только для объекта: `Headers` растекается в `{}` (нет собственных
    // перечисляемых свойств), массив пар — в `{0: [...], 1: [...]}`. То есть заголовки
    // вызывающего молча терялись бы. `new Headers()` нормализует все три формы.
    const headers = new Headers(options.headers);
    for (const [key, value] of Object.entries(authHeaders)) {
      headers.set(key, value);
    }

    // Ставим Content-Type только когда есть тело и оно не FormData.
    // Без этой проверки DELETE-запросы отправляются с Content-Type: application/json
    // при пустом теле, что приводит к ошибке парсинга на Fastify (400 Bad Request).
    if (options.body !== undefined && !(options.body instanceof FormData)) {
      headers.set('Content-Type', 'application/json');
    }

    const init: RequestInit = { ...options, credentials: 'include', headers };
    return REFRESH_COOKIE_ENDPOINTS.some((endpoint) => path.startsWith(endpoint))
      ? serializeRefreshCookieWrite((signal) => fetch(`${API_BASE}${path}`, { ...init, signal }))
      : fetch(`${API_BASE}${path}`, init);
  }, path);

  if (isAuthEndpoint(path) && res.status === 401) {
    const err = await readErrorBody(res, editorText('Ошибка авторизации'));
    throw new ApiRequestError(
      (err as { error?: string }).error || editorText('Ошибка авторизации'),
      (err as { code?: string }).code,
    );
  }

  if (!res.ok) {
    const err = await readErrorBody(res, editorText('Ошибка запроса'));
    maybeOpenPaywall(err);
    throw new ApiRequestError(
      (err as { error?: string }).error || editorText('Ошибка запроса'),
      (err as { code?: string }).code,
      (err as { conflicts?: string[] }).conflicts,
      res.status,
    );
  }

  if (res.status === 204) {
    return undefined;
  }

  const text = await res.text();
  if (!text) {
    return undefined;
  }
  return JSON.parse(text);
}

async function fetchApiBlob(path: string): Promise<Blob> {
  const res = await withAuthRetry(
    (authHeaders) =>
      fetch(`${API_BASE}${path}`, { credentials: 'include', headers: authHeaders }),
    path,
  );

  if (!res.ok) {
    const err = await readErrorBody(res, editorText('Ошибка запроса'));
    throw new ApiRequestError(
      (err as { error?: string }).error || editorText('Ошибка запроса'),
      (err as { code?: string }).code,
      (err as { conflicts?: string[] }).conflicts,
    );
  }

  return res.blob();
}

/** Как fetchApiBlob, но POST + JSON body — выгрузка выделения по списку id (§5). */
async function fetchApiBlobPost(path: string, body: unknown): Promise<Blob> {
  const res = await withAuthRetry((authHeaders) => {
    const headers = new Headers(authHeaders);
    headers.set('Content-Type', 'application/json');
    return fetch(`${API_BASE}${path}`, {
      method: 'POST',
      credentials: 'include',
      headers,
      body: JSON.stringify(body),
    });
  }, path);

  if (!res.ok) {
    const err = await readErrorBody(res, editorText('Ошибка запроса'));
    throw new ApiRequestError(
      (err as { error?: string }).error || editorText('Ошибка запроса'),
      (err as { code?: string }).code,
      (err as { conflicts?: string[] }).conflicts,
    );
  }

  return res.blob();
}

export const api = {
  /**
   * `signal` — для отмены устаревшего запроса (TanStack Query передаёт свой). `headers` — для
   * ключа, которому не место в адресе (`clientId` гостя в публичных комментариях).
   */
  get: (path: string, options?: { signal?: AbortSignal; headers?: Record<string, string> }) =>
    fetchApi(path, {
      ...(options?.signal ? { signal: options.signal } : {}),
      ...(options?.headers ? { headers: options.headers } : {}),
    }),

  getBlob: (path: string) => fetchApiBlob(path),

  postBlob: (path: string, body: unknown) => fetchApiBlobPost(path, body),

  post: (path: string, body?: unknown) =>
    fetchApi(path, {
      method: 'POST',
      body: body instanceof FormData ? body : JSON.stringify(body),
    }),

  put: (path: string, body?: unknown) =>
    fetchApi(path, {
      method: 'PUT',
      body: JSON.stringify(body),
    }),

  patch: (path: string, body?: unknown) =>
    fetchApi(path, {
      method: 'PATCH',
      body: JSON.stringify(body),
    }),

  delete: (path: string, body?: unknown, options?: { headers?: Record<string, string> }) =>
    fetchApi(path, {
      method: 'DELETE',
      ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
      ...(options?.headers ? { headers: options.headers } : {}),
    }),

  /**
   * DELETE with keepalive: true — use for cleanup in beforeunload/unload
   * handlers where the browser may cancel regular fetch requests.
   * Fire-and-forget: returns void, errors are silently ignored.
   */
  deleteKeepalive: (path: string) => {
    const token = useAuthStore.getState().accessToken;
    const headers: Record<string, string> = {};
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }
    try {
      void fetch(`${API_BASE}${path}`, { method: 'DELETE', keepalive: true, credentials: 'include', headers });
    } catch {
      // keepalive fetch may throw in some environments; silently ignore
    }
  },

  /**
   * POST FormData with `keepalive: true` — рассчитан на fire-and-forget отправку
   * в обработчиках `visibilitychange` / unmount (напр. превью проекта). Без
   * refresh-retry, Bearer берём из текущего `authStore`.
   */
  postFormKeepalive: (path: string, form: FormData) => {
    const token = useAuthStore.getState().accessToken;
    const headers: Record<string, string> = {};
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }
    try {
      void fetch(`${API_BASE}${path}`, {
        method: 'POST',
        body: form,
        keepalive: true,
        credentials: 'include',
        headers,
      });
    } catch {
      // keepalive fetch may throw in some environments; silently ignore
    }
  },

  /**
   * Generates a custom map style from a natural language description.
   * Returns a style patch and suggested theme name.
   */
  generateMapStyle: (description: string) =>
    fetchApi('/ai/generate-map-style', {
      method: 'POST',
      body: JSON.stringify({ description }),
    }),

  getProjectMapStyles: (projectId: string) =>
    fetchApi(`/projects/${projectId}/map-styles`) as Promise<MapStyleRecord[]>,

  createProjectMapStyle: (projectId: string, name: string, patch: unknown) =>
    fetchApi(`/projects/${projectId}/map-styles`, {
      method: 'POST',
      body: JSON.stringify({ name, patch }),
    }) as Promise<MapStyleRecord>,

  deleteProjectMapStyle: (projectId: string, styleId: string) =>
    fetchApi(`/projects/${projectId}/map-styles/${styleId}`, { method: 'DELETE' }),

  updateProjectMapStyle: (
    projectId: string,
    styleId: string,
    body: { name?: string; patch?: unknown },
  ) =>
    fetchApi(`/projects/${projectId}/map-styles/${styleId}`, {
      method: 'PUT',
      body: JSON.stringify(body),
    }) as Promise<MapStyleRecord>,

  getUserMapStyles: () => fetchApi('/user/map-styles') as Promise<UserMapStyleRecord[]>,

  importProjectMapStyle: (projectId: string, sourceStyleId: string) =>
    fetchApi(`/projects/${projectId}/map-styles/import`, {
      method: 'POST',
      body: JSON.stringify({ sourceStyleId }),
    }) as Promise<MapStyleRecord>,
};
